import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef} from '@angular/core';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {Subject} from 'rxjs';
import {switchMap, takeUntil} from 'rxjs/operators';
import {MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from '@angular/material/legacy-dialog';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {
    DirectoryUser,
    User,
    UserMfaConfig,
    UserMfaConfigFriendlyNameMap,
    UserMfaConfigName
} from '@app/core/model/user';
import {VerifyPhoneDialogComponent, SmsMfaDialogViews} from '@app/core/comp/dialog/verify-phone-dialog/verify-phone-dialog.component';
import {MESSAGES} from '@app/core/model/messages';
import {copyData} from '@app/core/model/integration-models';
import {DirectoryOrganization} from '@app/core/model/entity-profile';
import {TotpMfaSetupDialogComponent} from '@app/core/comp/dialog/totp-mfa-setup-dialog/totp-mfa-setup-dialog.component';
import {ConfirmDialogComponent} from '@app/core/comp/dialog/confirm-dialog/confirm-dialog.component';
import {AuditUserActivity, AuditUserActivityActions} from '@app/core/model/audit-user-activity.model';
import {AuditUserActivityService} from '@app/core/service/audit-user-activity.service';
import {environment} from '@env/environment';

@Component({
    selector: 'ngc-user-mfa-settings',
    styleUrls: ['user-mfa-settings.scss'],
    templateUrl: 'user-mfa-settings.html'
})
export class UserMfaSettingsComponent implements OnInit, OnDestroy {

    @Input() user: User;
    @Input() userOrg: DirectoryOrganization;
    @Input() isCurrentUser = true;
    @Output() hasRecoveryCodes: EventEmitter<boolean> = new EventEmitter();

    userMfaConfigSettings = {
        [UserMfaConfigName.SMS]: <MfaConfigUIProperties>{
            friendlyName: UserMfaConfigFriendlyNameMap.get(UserMfaConfigName.SMS),
            icon: 'sms',
            description: 'Receive login codes via text message (SMS).'
        },
        [UserMfaConfigName.TOTP]: <MfaConfigUIProperties>{
            friendlyName: UserMfaConfigFriendlyNameMap.get(UserMfaConfigName.TOTP),
            icon: 'apps',
            description: 'Use Google Authenticator or similar app to generate login codes.'
        }
    };

    private unsubscribe$ = new Subject<void>();

    constructor(
        private snackBar: MatSnackBar,
        private authenticatedUserService: AuthenticatedUserService,
        private auditUserActivityService: AuditUserActivityService,
        private viewContainerRef: ViewContainerRef,
        private matDialog: MatDialog
    ) {
    }

    @Input() set userData(userData: DirectoryUser) {
        this.user = User.convertDirectoryUserToUser(userData);
    }

    ngOnInit() {
        this.checkRecoveryCodesExisting();
    }

    onEditMfa(mfaConfig: UserMfaConfig): void {
        this.showSetupMfaDialog(mfaConfig, true, (result, successMsg: string) => {
            if (result) {
                mfaConfig.enabled = true;
                this.snackBar.open(successMsg, '', <MatSnackBarConfig>{duration: 1500});
            }
        });
    }

    onChangeMfaFactor(event: any, mfaConfig: UserMfaConfig) {
        event.preventDefault();
        event.stopPropagation();

        if (mfaConfig.enabled) {
            this.disableMfaFactor(mfaConfig);
        } else if (this.isCurrentUser) {
            this.showSetupMfaDialog(mfaConfig, false, (result, successMsg: string) => {
                if (result) {
                    mfaConfig.enabled = true;
                    this.snackBar.open(successMsg, '', <MatSnackBarConfig>{duration: 1500});
                }
            });
        }
    }

    ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private showSetupMfaDialog(
        mfaConfig: UserMfaConfig,
        isEdit: boolean,
        callback: (any, string) => any
    ): void {
        if (mfaConfig.name === UserMfaConfigName.TOTP) {
            const successMsg = (isEdit ? 'Authenticator app updated.' : 'Authenticator app enabled.');
            this.showTotpMfaSetupDialog(callback, successMsg);
        } else if (mfaConfig.name === UserMfaConfigName.SMS) {
            const successMsg = (isEdit ? 'Phone number updated.' : 'SMS verification enabled.');
            this.showSmsMfaSetupDialog(callback, successMsg);
        }
    }

    private showTotpMfaSetupDialog(
        callback: (any, string) => any,
        successMsg: string
    ): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '450px';
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;

        const dialogRef = this.matDialog.open(TotpMfaSetupDialogComponent, dialogConfig);

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
            .subscribe((result) =>
                callback(result, successMsg)
            );
    }

    private showSmsMfaSetupDialog(
        callback: (any, string) => any,
        successMsg: string
    ): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.width = '570px';
        dialogConfig.data = {
            user: this.user,
            orgCountry: this.userOrg.country,
            view: SmsMfaDialogViews.InputPhone
        };
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;

        const dialogRef = this.matDialog.open(VerifyPhoneDialogComponent, dialogConfig);

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
            .subscribe((result) => {
                callback(result, successMsg);
                if (result) {
                    this.authenticatedUserService.refreshAuthenticatedUser();
                }
            });
    }

    private disableMfaFactor(mfaConfig: UserMfaConfig): void {
        if (mfaConfig.name === UserMfaConfigName.TOTP) {
            this.disableTotpMfaFactor(mfaConfig);
        } else if (mfaConfig.name === UserMfaConfigName.SMS) {
            this.disableSmsMfaFactor(mfaConfig);
        }
    }

    private disableTotpMfaFactor(mfaConfig: UserMfaConfig): void {
        this.openConfirmationDialog(
            'Remove MFA Method',
            `Are you sure you want to remove Authenticator App method?`,
            () => {
                this.authenticatedUserService.disableTotpMfaForUser(this.user.guid)
                    .pipe(
                        switchMap(() =>
                            this.auditUserActivityService.createUserAction(<AuditUserActivity>{
                                orgGuid: this.user.orgGuid,
                                objectId: this.user.guid,
                                actionName: AuditUserActivityActions.DISABLE_TOTP_MFA
                            })
                        ),
                        takeUntil(this.unsubscribe$)
                    ).subscribe(() => {
                        this.changeMfaConfigStatus(mfaConfig, false);
                        this.snackBar.open('Authenticator app MFA successfully removed.', '', <MatSnackBarConfig>{duration: 1500});
                    },
                    err => {
                        this.changeMfaConfigStatus(mfaConfig, true);
                        this.snackBar.open(MESSAGES.UNKNOWN_ERROR, 'ERROR', <MatSnackBarConfig>{duration: 2000});
                    });
            });
    }

    private disableSmsMfaFactor(mfaConfig: UserMfaConfig): void {
        this.openConfirmationDialog(
            'Remove MFA Method',
            `Are you sure you want to remove SMS verification method?`,
            () => {
                this.disableUserMFA(() =>
                    this.changeMfaConfigStatus(mfaConfig, false)
                );
            });
    }

    private openConfirmationDialog(header: string, msg: string, callback: () => any) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.viewContainerRef = this.viewContainerRef;
        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;

        const dialogRef = this.matDialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.componentInstance.item = 'other';
        dialogRef.componentInstance.header = header;
        dialogRef.componentInstance.target = msg;

        dialogRef.afterClosed().pipe(takeUntil(this.unsubscribe$))
            .subscribe(result => {
                if (result === 'ok') {
                    callback();
                }
            });
    }

    private disableUserMFA(callback?: () => any) {
        const user: User = copyData(this.user);
        user.mfaEnabled = false;
        if (this.user.mfaEnabled) {
            const disableUserMFA$ = ((!environment.stsServer || this.authenticatedUserService.isVendor(this.user))
                ? this.authenticatedUserService.disableUserMFA(user)
                : this.authenticatedUserService.disableUserMFAFromPassportAuthService(user.guid));
            disableUserMFA$.pipe(
                switchMap(() =>
                    this.auditUserActivityService.createUserAction(<AuditUserActivity>{
                        orgGuid: user.orgGuid,
                        objectId: user.guid,
                        actionName: AuditUserActivityActions.DISABLE_MFA
                    })
                ),
                takeUntil(this.unsubscribe$)
            ).subscribe((updatedUser: User) => {
                if (this.isCurrentUser) {
                    this.authenticatedUserService.refreshAuthenticatedUser();
                }

                if (callback) {
                    callback();
                }
                this.snackBar.open('SMS verification MFA successfully removed.', '', <MatSnackBarConfig>{duration: 1500});
            }, err => {
                this.snackBar.open(MESSAGES.UNKNOWN_ERROR, 'ERROR', <MatSnackBarConfig>{duration: 2000});
            });
        }
    }

    private changeMfaConfigStatus(mfaConfig: UserMfaConfig, isEnabled: boolean) {
        mfaConfig.enabled = isEnabled;
        this.checkRecoveryCodesExisting();
    }

    private checkRecoveryCodesExisting() {
        const hasRecoveryCodes = this.user.factorsList
            ?.some((factor: UserMfaConfig) =>
                factor.enabled
            );
        this.hasRecoveryCodes.emit(hasRecoveryCodes);
    }
}

class MfaConfigUIProperties {
    friendlyName: string;
    icon: string;
    description: string;
}
